import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import Utils from '../../../utils/immersive_utils';
import { DataLabelEnum } from '../../../model/data_label.enum';
import { SourceTypeEnum } from '../../../model/source_type.enum';
import { CandleRequest, CandleSubscriptionRequest } from '../../model/candleData';
import { PERPETUAL_HISTORICAL_ID, PERPETUAL_ID } from '../market-data.constants';
import { RangeEnum } from '../../../model/range.enum';
import { WebSocketContext } from '../../websocket-context/websocket-provider';
import { SUBSCRIPTIONS_KEY } from '../../../constants/websocket-events';

export type IFetchPerpetualHistorical = {
  endDate: Date;
  range: RangeEnum;
  options?: { isBackward?: boolean; calendarStart?: Date };
};

export function usePerpetualData(
  currency: string,
  providerKey: string | undefined,
  updateSeries: (id: string, data: any[], opts?: { isBackward?: boolean }) => void,
  appendSeriesTick: (id: string, tick: any) => void,
) {
  const { sendMessage, subscribeToChannel, isConnected } = useContext(WebSocketContext);
  const [loading, setLoading] = useState(false);
  const [isFetchingPerpetualSeries, setIsFetchingPerpetualSeries] = useState(false);
  const rangeRef = useRef<RangeEnum>(RangeEnum.D1);

  const generateId = (id: string) => `${id}_${currency}${providerKey ? `-${providerKey}` : ''}`;

  const fetchPerpetualHistorical = useCallback(
    ({ endDate, range, options }: IFetchPerpetualHistorical) => {
      if (!isConnected) return;
      if (isFetchingPerpetualSeries) return;

      setIsFetchingPerpetualSeries(true);
      const isBackward = options?.isBackward ?? false;
      rangeRef.current = range;
      const interval = Utils.getIntervalByRange(range);
      const init = options?.calendarStart ?? Utils.getInitDateByRange(endDate, range);
      const to = isBackward ? endDate : Utils.updateDateToInterval(endDate, interval);
      const id = isBackward ? generateId(PERPETUAL_HISTORICAL_ID) : generateId(PERPETUAL_ID);
      const req = new CandleRequest(
        id,
        init.toISOString(),
        to.toISOString(),
        interval,
        SourceTypeEnum.DERIBIT,
        currency!,
        DataLabelEnum.PERPETUAL,
      );

      try {
        sendMessage(JSON.stringify(req));
      } catch {
        setLoading(false);
        setIsFetchingPerpetualSeries(false);
        return;
      }

      return new Promise<void>((resolve) => {
        subscribeToChannel(id, (resp: any) => {
          setLoading(false);
          if (!resp.result) {
            setIsFetchingPerpetualSeries(false);
            return resolve();
          }
          const raw = resp.result as number[][];
          const points = raw.map((r) => ({
            time: moment.utc(r[0], 'YYYY-MM-DD HH:mm:ss').unix(),
            open: r[1],
            high: r[2],
            low: r[3],
            close: r[4],
            value: r[4],
          }));

          if (isBackward) {
            updateSeries(PERPETUAL_ID, points, { isBackward: true });
          } else {
            updateSeries(PERPETUAL_ID, points);
          }
          setIsFetchingPerpetualSeries(false);
          resolve();
        });
      });
    },
    [
      currency,
      providerKey,
      isConnected,
      loading,
      sendMessage,
      updateSeries,
      isFetchingPerpetualSeries,
    ],
  );

  const subscribeLive = useCallback(() => {
    if (!isConnected) return;
    const now = new Date();
    now.setSeconds(0);
    now.setMilliseconds(0);
    const req = new CandleSubscriptionRequest(
      generateId(SUBSCRIPTIONS_KEY.CANDLE_SUBSCRIPTION),
      now.toISOString(),
      '00:01:00',
      SourceTypeEnum.DERIBIT,
      currency!,
      DataLabelEnum.PERPETUAL,
    );
    sendMessage(JSON.stringify(req));
    subscribeToChannel(generateId(SUBSCRIPTIONS_KEY.CANDLE_SUBSCRIPTION), (resp: any) => {
      if (!resp.params?.data) return;
      const [raw] = resp.params.data as any[][];
      const ts = moment.utc(raw[0], 'YYYY-MM-DD HH:mm:ss').unix();
      const tick =
        raw.length > 2
          ? { time: ts, open: raw[1], high: raw[2], low: raw[3], close: raw[4], value: raw[4] }
          : { time: ts, value: raw[1] };
      appendSeriesTick(PERPETUAL_ID, tick);
    });
  }, [currency, providerKey, isConnected, appendSeriesTick]);

  useEffect(() => {
    return () => {
      setLoading(false);
    };
  }, []);

  return {
    loading,
    setLoading,
    isFetchingPerpetualSeries,
    fetchPerpetualHistorical,
    subscribeLive,
  };
}
